// Tile faces drawn as inline SVG (no external images needed).
import type { Tile } from "./types";

export const kindOf = (t: Tile) => t >> 2;
export const isRed = (t: Tile) => t === 16 || t === 52 || t === 88;

const BLUE = "#1b4f9c";
const GREEN = "#1d7a3a";
const RED = "#c62828";
const INK = "#1d1d1f";

const KANJI_NUM = ["一", "二", "三", "四", "五", "六", "七", "八", "九"];
const HONOR = ["東", "南", "西", "北", "白", "發", "中"];
const HONOR_HINT = ["В", "Ю", "З", "С", "", "", ""];

function circle(x: number, y: number, r: number, color: string): string {
  return `<circle cx="${x}" cy="${y}" r="${r}" fill="none" stroke="${color}" stroke-width="${r * 0.32}"/>` +
    `<circle cx="${x}" cy="${y}" r="${r * 0.38}" fill="${color}"/>`;
}

function stick(x: number, y: number, h: number, color: string, w = 7): string {
  const top = y - h / 2;
  return `<rect x="${x - w / 2}" y="${top}" width="${w}" height="${h}" rx="${w / 2}" fill="${color}"/>` +
    `<line x1="${x - w / 2 + 1}" y1="${y}" x2="${x + w / 2 - 1}" y2="${y}" stroke="#fff" stroke-width="1.4"/>`;
}

type P = [number, number, string];

function pinLayout(n: number, red: boolean): string {
  const c = (col: string) => (red ? RED : col);
  let pts: P[] = [];
  let r = 8;
  switch (n) {
    case 1:
      return circle(30, 40, 19, c(RED)) + circle(30, 40, 9, c(GREEN));
    case 2: pts = [[30, 23, GREEN], [30, 57, BLUE]]; r = 11; break;
    case 3: pts = [[15, 18, BLUE], [30, 40, RED], [45, 62, GREEN]]; r = 9; break;
    case 4: pts = [[18, 24, BLUE], [42, 24, GREEN], [18, 56, GREEN], [42, 56, BLUE]]; r = 10; break;
    case 5: pts = [[17, 20, BLUE], [43, 20, GREEN], [30, 40, RED], [17, 60, GREEN], [43, 60, BLUE]]; r = 9; break;
    case 6: pts = [[18, 16, GREEN], [42, 16, GREEN], [18, 42, RED], [42, 42, RED], [18, 64, RED], [42, 64, RED]]; r = 9; break;
    case 7: pts = [[14, 13, GREEN], [30, 21, GREEN], [46, 29, GREEN], [18, 49, RED], [42, 49, RED], [18, 67, RED], [42, 67, RED]]; r = 7.5; break;
    case 8: pts = [[18, 13, BLUE], [42, 13, BLUE], [18, 31, BLUE], [42, 31, BLUE], [18, 49, BLUE], [42, 49, BLUE], [18, 67, BLUE], [42, 67, BLUE]]; r = 7.5; break;
    case 9:
      for (const [y, col] of [[17, BLUE], [40, RED], [63, GREEN]] as [number, string][])
        for (const x of [14, 30, 46]) pts.push([x, y, col]);
      r = 7.5;
      break;
  }
  return pts.map(([x, y, col]) => circle(x, y, r, c(col))).join("");
}

function souLayout(n: number, red: boolean): string {
  const c = (col: string) => (red ? RED : col);
  const s: string[] = [];
  const add = (x: number, y: number, h: number, col = GREEN) => s.push(stick(x, y, h, c(col)));
  switch (n) {
    case 1:
      // A stylized bird: body + head + tail.
      return `<ellipse cx="30" cy="46" rx="13" ry="17" fill="${c(GREEN)}"/>` +
        `<circle cx="30" cy="22" r="8" fill="${c(RED)}"/>` +
        `<path d="M22 60 L14 74 M30 62 L30 76 M38 60 L46 74" stroke="${c(BLUE)}" stroke-width="4" stroke-linecap="round"/>` +
        `<circle cx="27" cy="20" r="1.8" fill="#fff"/>`;
    case 2: add(30, 23, 24); add(30, 57, 24, BLUE); break;
    case 3: add(30, 23, 24); add(18, 57, 24, BLUE); add(42, 57, 24, BLUE); break;
    case 4: add(18, 23, 24); add(42, 23, 24, BLUE); add(18, 57, 24, BLUE); add(42, 57, 24); break;
    case 5: add(15, 23, 24); add(45, 23, 24, BLUE); add(30, 40, 24, RED); add(15, 57, 24, BLUE); add(45, 57, 24); break;
    case 6: for (const x of [14, 30, 46]) { add(x, 23, 24); add(x, 57, 24, BLUE); } break;
    case 7: add(30, 14, 18, RED); for (const x of [14, 30, 46]) { add(x, 41, 18); add(x, 66, 18, BLUE); } break;
    case 8: for (const x of [11, 23, 37, 49]) { add(x, 23, 24); add(x, 57, 24, BLUE); } break;
    case 9:
      for (const y of [16, 40, 64]) for (const x of [14, 30, 46]) add(x, y, 19, x === 30 ? RED : GREEN);
      break;
  }
  return s.join("");
}

function face(kind: number, red: boolean): string {
  if (kind < 9) {
    const n = kind + 1;
    return `<text x="30" y="35" class="kj" font-size="27" text-anchor="middle" fill="${red ? RED : INK}">${KANJI_NUM[n - 1]}</text>` +
      `<text x="30" y="70" class="kj" font-size="27" text-anchor="middle" fill="${RED}">萬</text>` +
      `<text x="5" y="13" class="hint" font-size="11" fill="${red ? RED : INK}">${n}</text>`;
  }
  if (kind < 18) return pinLayout(kind - 8, red);
  if (kind < 27) return souLayout(kind - 17, red);
  const h = kind - 27;
  if (h === 4) {
    return `<rect x="13" y="16" width="34" height="48" rx="3" fill="none" stroke="${BLUE}" stroke-width="3.5"/>`;
  }
  const col = h === 5 ? GREEN : h === 6 ? RED : INK;
  const hint = HONOR_HINT[h] ? `<text x="5" y="13" class="hint" font-size="11" fill="${INK}">${HONOR_HINT[h]}</text>` : "";
  return `<text x="30" y="54" class="kj" font-size="38" text-anchor="middle" fill="${col}">${HONOR[h]}</text>` + hint;
}

const cache = new Map<string, string>();

export function tileSvg(t: Tile, sideways = false): string {
  const kind = kindOf(t);
  const red = isRed(t);
  const key = `${kind}-${red}-${sideways}`;
  let svg = cache.get(key);
  if (!svg) {
    const content = face(kind, red);
    svg = sideways
      ? `<svg viewBox="0 0 80 60" preserveAspectRatio="xMidYMid meet"><g transform="translate(80 0) rotate(90)">${content}</g></svg>`
      : `<svg viewBox="0 0 60 80" preserveAspectRatio="xMidYMid meet">${content}</svg>`;
    cache.set(key, svg);
  }
  return svg;
}

export interface TileOpts {
  sideways?: boolean;
  cls?: string;
  attrs?: string;
}

export function tileHtml(t: Tile, o: TileOpts = {}): string {
  const cls = ["tile", o.sideways ? "side" : "", isRed(t) ? "red" : "", o.cls || ""].filter(Boolean).join(" ");
  return `<span class="${cls}" ${o.attrs || ""}>${tileSvg(t, o.sideways)}</span>`;
}

export function backHtml(cls = ""): string {
  return `<span class="tile back ${cls}"></span>`;
}

const SUITS = ["m", "p", "s"];
export function kindName(k: number): string {
  if (k >= 27) return HONOR[k - 27];
  return `${(k % 9) + 1}${SUITS[Math.floor(k / 9)]}`;
}

export function kindTileHtml(k: number, cls = ""): string {
  return tileHtml(k * 4 + (k === 4 || k === 13 || k === 22 ? 1 : 0), { cls });
}
